import { useState, useEffect, useRef, useMemo } from 'react'
import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import F1Logo from '@components/ui/F1Logo'
import usePageSeo from '@hooks/usePageSeo'
import { getPeakRankings, getCurrentGrid } from '@services/staticData'
import './Home.css'

const plateIn = {
  hidden: { opacity: 0, y: 16 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.55, ease: [0.25, 0.46, 0.45, 0.94] } },
}

const COMPARE_BARS_A = [88, 95, 92, 100, 96]
const COMPARE_BARS_B = [96, 90, 100, 92, 88]
const DECADES = [1950, 1960, 1970, 1980, 1990, 2000, 2010, 2020]

const ERA_LABELS = {
  1950: 'The Fangio Era',
  1960: 'The Clark Era',
  1970: 'The Stewart Era',
  1980: 'The Turbo Era',
  1990: 'The Senna Era',
  2000: 'The Schumacher Era',
  2010: 'The Hamilton Era',
  2020: 'The Verstappen Era',
}

const Home = () => {
  usePageSeo({
    title: 'F1 ELO Rankings | Formula 1 Driver Ratings Across Eras',
    description: 'Track Formula 1 driver Elo ratings from 1950 to 2026, with all-time peaks, current-grid ratings, race-by-race history, and comparison tools.',
    path: '/',
  })

  const [topDrivers, setTopDrivers] = useState([])
  const [currentGrid, setCurrentGrid] = useState([])
  const [activeDecade, setActiveDecade] = useState(1950)
  const [statIndex, setStatIndex] = useState(0)
  const timelinePausedRef = useRef(false)

  const gridLeader = useMemo(() => {
    if (!currentGrid.length) return null
    return [...currentGrid].sort((a, b) => (b.currentElo || 0) - (a.currentElo || 0))[0]
  }, [currentGrid])

  const coverStats = useMemo(() => {
    const stats = ['699 drivers · 1950 to 2026 · 24,946 race results']
    if (topDrivers[0]) {
      const lastName = topDrivers[0].name.split(' ').pop()
      stats.push(`${lastName} holds the all-time peak · ${Math.round(topDrivers[0].peakElo).toLocaleString()} Elo`)
    }
    if (gridLeader) {
      const lastName = gridLeader.name.split(' ').pop()
      stats.push(`${lastName} leads the current grid · ${Math.round(gridLeader.currentElo).toLocaleString()} Elo`)
    }
    return stats
  }, [topDrivers, gridLeader])

  useEffect(() => {
    getPeakRankings()
      .then(data => setTopDrivers((data || []).slice(0, 5)))
      .catch(() => {})
    getCurrentGrid()
      .then(data => setCurrentGrid(data || []))
      .catch(() => {})
  }, [])

  // Stable interval — uses ref so mouse events don't restart the clock
  useEffect(() => {
    const id = setInterval(() => {
      if (timelinePausedRef.current) return
      setActiveDecade(prev => {
        const idx = DECADES.indexOf(prev)
        return DECADES[(idx + 1) % DECADES.length]
      })
    }, 2200)
    return () => clearInterval(id)
  }, [])

  useEffect(() => {
    if (coverStats.length <= 1) return
    const id = setInterval(() => setStatIndex(i => (i + 1) % coverStats.length), 3500)
    return () => clearInterval(id)
  }, [coverStats.length])

  const maxElo = topDrivers[0]?.peakElo ?? 1900

  return (
    <div className="home">

      {/* ── Cover ── */}
      <section className="cover">
        <div className="cover-inner">
          <span className="cover-label">Formula 1 Performance Archive</span>
          <div className="cover-rule" />
          <h1 className="cover-headline">
            <span className="cover-headline-line">
              <F1Logo className="cover-f1-logo" color="currentColor" ariaLabel="Formula 1" />
              {' '}Driver
            </span>
            <span className="cover-headline-line">Rankings</span>
          </h1>
          <div className="cover-rule" />
          <motion.p
            key={statIndex}
            className="cover-stats"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            {coverStats[statIndex]}
          </motion.p>
          <div className="cover-cta">
            <Link to="/all-time" className="cta-primary">View Rankings →</Link>
            <Link to="/analysis" className="cta-secondary">Read Analysis →</Link>
          </div>
        </div>
        <button
          className="scroll-cue"
          onClick={() => document.getElementById('plates')?.scrollIntoView({ behavior: 'smooth' })}
          aria-label="Scroll to features"
        >
          <svg width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden="true">
            <path d="M5 8.5L11 14.5L17 8.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="square" strokeLinejoin="miter"/>
          </svg>
        </button>
      </section>

      {/* ── Editorial Plates ── */}
      <section className="plates" id="plates">

        {/* FIG. 01 — All-Time Rankings */}
        <motion.article
          className="plate"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-60px' }}
          variants={plateIn}
        >
          <div className="plate-text">
            <span className="fig-label">FIG. 01</span>
            <div className="plate-rule" />
            <h2 className="plate-title">All-Time Rankings</h2>
            <p className="plate-caption">
              Every Formula 1 driver in the archive is ranked by career peak Elo.
              Fangio, Hamilton, Verstappen, and the full field sit on the same scale.
            </p>
            <Link to="/all-time" className="plate-cta">View Full Rankings →</Link>
          </div>
          <div className="plate-visual">
            <div className="elo-chart">
              {(topDrivers.length > 0 ? topDrivers : Array(5).fill(null)).map((driver, i) => {
                const rowContent = (
                  <>
                    <span className="elo-rank">#{i + 1}</span>
                    <span className={`elo-name${!driver ? ' faded' : ''}`}>
                      {driver ? driver.name.split(' ').pop() : 'Loading'}
                    </span>
                    <div className="elo-track">
                      {driver ? (
                        <motion.div
                          className="elo-bar"
                          initial={{ width: 0 }}
                          whileInView={{ width: `${(driver.peakElo / maxElo) * 100}%` }}
                          viewport={{ once: true }}
                          transition={{ duration: 0.75, delay: i * 0.08, ease: 'easeOut' }}
                        />
                      ) : (
                        <div className="elo-bar faded" style={{ width: `${85 - i * 5}%` }} />
                      )}
                    </div>
                    <span className={`elo-value${!driver ? ' faded' : ''}`}>
                      {driver ? Math.round(driver.peakElo) : '—'}
                    </span>
                  </>
                )
                return driver ? (
                  <Link
                    key={driver.id}
                    to={`/drivers/${driver.id.replace(/_/g, '-')}`}
                    className="elo-row elo-row--link"
                  >
                    {rowContent}
                  </Link>
                ) : (
                  <div key={i} className="elo-row">{rowContent}</div>
                )
              })}
            </div>
          </div>
        </motion.article>

        {/* FIG. 02 — Driver Comparison */}
        <motion.article
          className="plate"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-60px' }}
          variants={plateIn}
        >
          <div className="plate-text">
            <span className="fig-label">FIG. 02</span>
            <div className="plate-rule" />
            <h2 className="plate-title">Driver Comparison</h2>
            <p className="plate-caption">
              Place two careers on one rating scale and inspect the gap by season.
              Built for teammates, title rivals, and cross-era comparisons.
            </p>
            <Link to="/comparison" className="plate-cta">Compare Drivers →</Link>
          </div>
          <div className="plate-visual">
            <div className="compare-graphic">
              <div className="compare-col">
                <Link to="/drivers/max-verstappen" className="compare-code compare-driver-link" aria-label="View Max Verstappen profile">VER</Link>
                <div className="compare-bars">
                  {COMPARE_BARS_A.map((h, i) => (
                    <motion.div
                      key={i}
                      className="compare-bar compare-bar--dark"
                      style={{ height: `${h}%`, transformOrigin: 'bottom' }}
                      initial={{ scaleY: 0 }}
                      whileInView={{ scaleY: 1 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.45, delay: i * 0.06, ease: 'easeOut' }}
                    />
                  ))}
                </div>
                <span className="compare-stat">Peak Elo 2159</span>
              </div>
              <Link to="/compare/max-verstappen-vs-lewis-hamilton" className="compare-vs compare-vs-link" aria-label="Compare Verstappen vs Hamilton">VS</Link>
              <div className="compare-col">
                <Link to="/drivers/lewis-hamilton" className="compare-code compare-driver-link" aria-label="View Lewis Hamilton profile">HAM</Link>
                <div className="compare-bars">
                  {COMPARE_BARS_B.map((h, i) => (
                    <motion.div
                      key={i}
                      className="compare-bar compare-bar--red"
                      style={{ height: `${h}%`, transformOrigin: 'bottom' }}
                      initial={{ scaleY: 0 }}
                      whileInView={{ scaleY: 1 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.45, delay: i * 0.06, ease: 'easeOut' }}
                    />
                  ))}
                </div>
                <span className="compare-stat">Peak Elo 2323</span>
              </div>
            </div>
          </div>
        </motion.article>

        {/* FIG. 03 — Historical Timeline */}
        <motion.article
          className="plate"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-60px' }}
          variants={plateIn}
        >
          <div className="plate-text">
            <span className="fig-label">FIG. 03</span>
            <div className="plate-rule" />
            <h2 className="plate-title">Historical Timeline</h2>
            <p className="plate-caption">
              Move through the archive by decade, then open the race-by-race view
              behind each era of Formula 1 ratings.
            </p>
            <Link to="/timeline" className="plate-cta">Explore Timeline →</Link>
          </div>
          <div className="plate-visual">
            <div className="timeline-ruler">
              <div className="ruler-track">
                {DECADES.map(year => (
                  <Link
                    key={year}
                    to={`/timeline?year=${year}`}
                    className={`ruler-tick${activeDecade === year ? ' ruler-tick--active' : ''}`}
                    onMouseEnter={() => { timelinePausedRef.current = true; setActiveDecade(year) }}
                    onMouseLeave={() => { timelinePausedRef.current = false }}
                  >
                    <div className="tick-mark" />
                    <span className="tick-label">{year}</span>
                  </Link>
                ))}
              </div>
              <motion.p
                key={activeDecade}
                className="ruler-era-label"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, ease: [0.25, 0.46, 0.45, 0.94] }}
              >
                {ERA_LABELS[activeDecade]}
              </motion.p>
              <p className="ruler-caption">RACE-BY-RACE ELO HISTORY · 24,946 RESULTS</p>
            </div>
          </div>
        </motion.article>

        {/* FIG. 04 — Active grid */}
        <motion.article
          className="plate"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-60px' }}
          variants={plateIn}
        >
          <div className="plate-text">
            <span className="fig-label">FIG. 04</span>
            <div className="plate-rule" />
            <h2 className="plate-title">Active Grid Ratings</h2>
            <p className="plate-caption">
              Every active driver in the latest grid snapshot, with current Elo
              ratings and career context one click away.
            </p>
            <Link to="/current-grid" className="plate-cta">View Current Grid →</Link>
          </div>
          <div className="plate-visual">
            <div className="grid-chips">
              {(currentGrid.length > 0
                ? currentGrid.slice(0, 20)
                : Array(20).fill(null)
              ).map((driver, i) =>
                driver ? (
                  <Link
                    key={driver.id}
                    to={`/drivers/${driver.id.replace(/_/g, '-')}`}
                    className="driver-chip"
                  >
                    {driver.code || driver.name?.split(' ').pop()?.slice(0, 3).toUpperCase() || '···'}
                  </Link>
                ) : (
                  <span key={i} className="driver-chip faded">···</span>
                )
              )}
            </div>
          </div>
        </motion.article>

        {/* FIG. 05 — Grid Leader */}
        <motion.article
          className="plate"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-60px' }}
          variants={plateIn}
        >
          <div className="plate-text">
            <span className="fig-label">FIG. 05</span>
            <div className="plate-rule" />
            <h2 className="plate-title">Grid Leader</h2>
            <p className="plate-caption">
              The active driver with the highest current Elo sits here.
              After each race, the rating update can change who holds this slot.
            </p>
            {gridLeader && (
              <Link to={`/drivers/${gridLeader.id.replace(/_/g, '-')}`} className="plate-cta">
                View Profile →
              </Link>
            )}
          </div>
          <div className="plate-visual">
            {gridLeader ? (
              <div className="featured-driver">
                <span className="featured-code">{gridLeader.code || gridLeader.name.split(' ').pop().slice(0, 3).toUpperCase()}</span>
                <p className="featured-name">{gridLeader.name}</p>
                <div className="featured-elo">
                  <span className="featured-elo-value">{Math.round(gridLeader.currentElo).toLocaleString()}</span>
                  <span className="featured-elo-label">Current Rating</span>
                </div>
                <div className="featured-stats">
                  <div className="featured-stat">
                    <span className="featured-stat-value">{Math.round(gridLeader.peakElo).toLocaleString()}</span>
                    <span className="featured-stat-label">Peak Rating</span>
                  </div>
                  <div className="featured-stat">
                    <span className="featured-stat-value">{gridLeader.totalRaces}</span>
                    <span className="featured-stat-label">Races</span>
                  </div>
                  <div className="featured-stat">
                    <span className="featured-stat-value">{gridLeader.careerStart}</span>
                    <span className="featured-stat-label">First Season</span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="featured-driver featured-driver--skeleton">
                <span className="featured-code faded">···</span>
              </div>
            )}
          </div>
        </motion.article>

      </section>

      {/* ── Discovery Strip ── */}
      <section className="discovery">
        <div className="discovery-inner">
          <span className="eyebrow">Explore the Archive</span>
          <div className="discovery-grid">
            <Link to="/all-time" className="discovery-tile">
              <span className="discovery-tile-eyebrow">Rankings</span>
              <h3 className="discovery-tile-title">All-Time</h3>
              <p className="discovery-tile-desc">699 drivers ranked by career peak Elo, with every era measured on one scale.</p>
              <span className="discovery-tile-cta">View Rankings →</span>
            </Link>
            <Link to="/timeline" className="discovery-tile">
              <span className="discovery-tile-eyebrow">History</span>
              <h3 className="discovery-tile-title">Timeline</h3>
              <p className="discovery-tile-desc">Race-by-race Elo movement from 1950 through 2026, covering 24,946 results.</p>
              <span className="discovery-tile-cta">Explore Timeline →</span>
            </Link>
            <Link to="/analysis" className="discovery-tile">
              <span className="discovery-tile-eyebrow">Editorial</span>
              <h3 className="discovery-tile-title">Analysis</h3>
              <p className="discovery-tile-desc">Data notes and driver profiles that explain what moved, what stayed stable, and how to read the model.</p>
              <span className="discovery-tile-cta">Read Analysis →</span>
            </Link>
            <Link to="/comparison" className="discovery-tile">
              <span className="discovery-tile-eyebrow">Tool</span>
              <h3 className="discovery-tile-title">Compare</h3>
              <p className="discovery-tile-desc">Put any two drivers on one chart and inspect the rating gap without changing scales.</p>
              <span className="discovery-tile-cta">Compare Drivers →</span>
            </Link>
          </div>
        </div>
      </section>
    </div>
  )
}

export default Home
